import React from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Code, 
  Globe2, 
  Server, 
  Database, 
  LayoutDashboard, 
  Layers, 
  ShoppingCart,
  MessageSquare, 
  Lock, 
  Zap, 
  BookOpen, 
  TrendingUp, 
  ShieldCheck, 
  BarChart2, 
  Package,
  ArrowLeft, 
  CheckCircle, 
  Lightbulb,
  Star,
  Users
} from 'lucide-react';

export default function WebSpecificServices() {
  const { serviceType } = useParams();

  const serviceData = {
    'mern-development': {
      title: 'MERN Stack Development',
      icon: Code,
      description: 'Build robust, scalable full-stack applications using MongoDB, Express.js, React, and Node.js. Perfect for modern web applications that require real-time functionality and seamless user experiences.',
      keyBenefits: [
        'Reusable React components for consistent UI',
        'Scalable Node.js APIs with Express framework',
        'Real-time updates with Socket.IO integration',
        'Secure authentication and middleware protection'
      ],
      useCases: [
        'Customer Relationship Management (CRM) systems',
        'Enterprise Resource Planning (ERP) platforms',
        'Real-time booking and reservation systems',
        'Social networking and collaboration platforms'
      ]
    },
    'nextjs-websites': {
      title: 'Next.js SSR Websites',
      icon: Globe2,
      description: 'Create lightning-fast, SEO-optimized websites using Next.js with server-side rendering, static generation, and automatic performance optimization for superior user experience.',
      keyBenefits: [
        'Automatic code splitting and lazy loading',
        'Incremental Static Regeneration (ISR) capabilities',
        'Built-in SEO optimization with meta management',
        'API routes for seamless backend integration'
      ],
      useCases: [
        'Corporate websites and landing pages',
        'E-commerce storefronts with dynamic content',
        'Blog platforms with content management',
        'Portfolio websites with interactive galleries'
      ]
    },
    'api-integration': {
      title: 'API Integration & Backend',
      icon: Server,
      description: 'Develop secure, high-performance REST and GraphQL APIs with comprehensive backend services, authentication systems, and seamless third-party integrations.',
      keyBenefits: [
        'RESTful API design with Express.js',
        'GraphQL endpoints for flexible data querying',
        'OAuth2 and JWT token-based authentication',
        'Webhook handling and real-time notifications'
      ],
      useCases: [
        'User authentication and authorization systems',
        'Payment gateway integration with Stripe/PayPal',
        'Third-party service API synchronization',
        'Custom webhook endpoints for automation'
      ]
    },
    'database-management': {
      title: 'Database Design & Management',
      icon: Database,
      description: 'Design and implement efficient database architectures using MongoDB, PostgreSQL, or MySQL with optimized queries, data modeling, and backup strategies.',
      keyBenefits: [
        'Optimized database schema design',
        'Advanced query optimization techniques',
        'Automated backup and recovery systems',
        'Data migration and synchronization tools'
      ],
      useCases: [
        'E-commerce product catalogs and inventory',
        'User profile and preference management',
        'Analytics and reporting data warehouses',
        'Content management system databases'
      ]
    },
    'admin-dashboards': {
      title: 'Admin Dashboards & Panels',
      icon: LayoutDashboard,
      description: 'Build comprehensive administrative interfaces with real-time analytics, user management, content control, and business intelligence visualization.',
      keyBenefits: [
        'Real-time data visualization and charts',
        'Role-based access control systems',
        'Responsive design for all devices',
        'Advanced filtering and search capabilities'
      ],
      useCases: [
        'Business analytics and KPI dashboards',
        'User management and permission systems',
        'Content moderation and approval workflows',
        'Sales and inventory tracking interfaces'
      ]
    },
    'ui-ux-design': {
      title: 'UI/UX Design & Implementation',
      icon: Layers,
      description: 'Create stunning, user-centered designs with modern aesthetics, intuitive navigation, and accessibility compliance for exceptional user experiences.',
      keyBenefits: [
        'Mobile-first responsive design approach',
        'Accessibility compliance (WCAG guidelines)',
        'Custom component libraries and design systems',
        'User testing and iterative improvement'
      ],
      useCases: [
        'Brand identity and visual design systems',
        'Mobile app interfaces and interactions',
        'Web accessibility improvements',
        'User journey optimization and conversion'
      ]
    },
    'ecommerce-solutions': {
      title: 'E-commerce Solutions',
      icon: ShoppingCart,
      description: 'Develop complete online shopping platforms with secure payment processing, inventory management, and customer relationship tools.',
      keyBenefits: [
        'Secure payment gateway integration',
        'Inventory and order management systems',
        'Customer account and wishlist features',
        'SEO-optimized product pages'
      ],
      useCases: [
        'Multi-vendor marketplace platforms',
        'Subscription-based service portals',
        'Digital product delivery systems',
        'B2B wholesale ordering platforms'
      ]
    },
    'chat-messaging': {
      title: 'Chat & Messaging Systems',
      icon: MessageSquare,
      description: 'Build real-time communication platforms with instant messaging, file sharing, group chats, and notification systems.',
      keyBenefits: [
        'Real-time messaging with Socket.IO',
        'File and media sharing capabilities',
        'Group chat and channel management',
        'Push notifications and alerts'
      ],
      useCases: [
        'Customer support chat systems',
        'Team collaboration platforms',
        'Social media messaging features',
        'Video conferencing integration'
      ]
    },
    'security-auth': {
      title: 'Security & Authentication',
      icon: Lock,
      description: 'Implement robust security measures with advanced authentication, authorization, data encryption, and compliance frameworks.',
      keyBenefits: [
        'Multi-factor authentication (MFA) systems',
        'Role-based permission management',
        'Data encryption and secure storage',
        'GDPR and compliance frameworks'
      ],
      useCases: [
        'Enterprise single sign-on (SSO) systems',
        'Financial application security',
        'Healthcare data protection (HIPAA)',
        'Government and defense applications'
      ]
    },
    'performance-optimization': {
      title: 'Performance Optimization',
      icon: Zap,
      description: 'Optimize web applications for speed, scalability, and efficiency with advanced caching, CDN integration, and performance monitoring.',
      keyBenefits: [
        'Advanced caching strategies and CDN setup',
        'Code splitting and lazy loading implementation',
        'Database query optimization',
        'Real-time performance monitoring'
      ],
      useCases: [
        'High-traffic website optimization',
        'Mobile app performance enhancement',
        'Server-side rendering improvements',
        'API response time optimization'
      ]
    },
    'cms-solutions': {
      title: 'CMS & Content Solutions',
      icon: BookOpen,
      description: 'Create flexible content management systems with intuitive editing interfaces, workflow management, and multi-language support.',
      keyBenefits: [
        'Intuitive content editing interfaces',
        'Workflow and approval systems',
        'Multi-language and localization support',
        'SEO optimization and meta management'
      ],
      useCases: [
        'Corporate blog and news platforms',
        'Educational content management',
        'Portfolio and gallery systems',
        'Documentation and knowledge bases'
      ]
    },
    'analytics-reporting': {
      title: 'Analytics & Reporting',
      icon: BarChart2,
      description: 'Implement comprehensive analytics solutions with custom dashboards, automated reporting, and business intelligence integration.',
      keyBenefits: [
        'Custom analytics dashboard creation',
        'Automated report generation',
        'Data visualization and charting',
        'Integration with Google Analytics and tools'
      ],
      useCases: [
        'Sales performance tracking systems',
        'Website traffic and user behavior analysis',
        'Marketing campaign effectiveness reports',
        'Financial reporting and forecasting'
      ]
    },'microservices-architecture': {
  title: 'Microservices & Deployment',
  icon: Layers,
  description: 'Design scalable, containerized applications using microservices architecture with Docker, Kubernetes, and robust CI/CD pipelines.',
  keyBenefits: [
    'Microservice orchestration with Docker and Kubernetes',
    'CI/CD automation with GitHub Actions or GitLab CI',
    'Independent service deployment and scaling',
    'Resilience and fault isolation in services'
  ],
  useCases: [
    'Enterprise-grade distributed systems',
    'SaaS product infrastructure',
    'Scalable backends for mobile/web apps',
    'DevOps pipelines and continuous deployment'
  ]
},
'realtime-apps': {
  title: 'Real-time Applications',
  icon: MessageSquare,
  description: 'Enable instant communication and live data synchronization using WebSockets, Server-Sent Events, or polling-based architectures.',
  keyBenefits: [
    'Instant messaging and data syncing',
    'WebSocket and SSE integrations',
    'Real-time dashboards and feeds',
    'Scalable publish/subscribe architecture'
  ],
  useCases: [
    'Live chat systems and chatbots',
    'Stock price and market dashboards',
    'IoT device communication',
    'Collaborative editing platforms'
  ]
},
'auth-systems': {
  title: 'Authentication Systems',
  icon: Lock,
  description: 'Secure web and mobile apps with modern authentication flows, including JWT, OAuth2, MFA, and role-based access.',
  keyBenefits: [
    'JWT and session-based authentication',
    'OAuth2 social logins and SSO',
    'Role and permission-based access control',
    'Encrypted user data and secure sessions'
  ],
  useCases: [
    'User login/registration systems',
    'Admin panel access control',
    'Multi-tenant SaaS products',
    'Enterprise access restriction models'
  ]
},
'pwa-development': {
  title: 'Progressive Web Apps (PWA)',
  icon: Package,
  description: 'Build installable web applications that work offline, support push notifications, and deliver native-like performance.',
  keyBenefits: [
    'Service workers for offline support',
    'Installable app experiences',
    'Push notifications and background sync',
    'Responsive design and performance boosts'
  ],
  useCases: [
    'Field service and delivery tools',
    'Offline-capable news apps',
    'E-commerce on mobile devices',
    'Progressive onboarding experiences'
  ]
},
'seo-optimization': {
  title: 'SEO Optimization',
  icon: TrendingUp,
  description: 'Improve site visibility on search engines using technical SEO strategies like structured data, meta tags, and sitemap configuration.',
  keyBenefits: [
    'Meta tag and Open Graph configuration',
    'robots.txt and sitemap generation',
    'Schema.org structured data',
    'Performance-focused SEO metrics'
  ],
  useCases: [
    'Blogs and publishing platforms',
    'E-commerce product pages',
    'Landing pages and campaigns',
    'Content-based lead generation sites'
  ]
},
'deployment-services': {
  title: 'Deployment & Hosting',
  icon: ShieldCheck,
  description: 'Automate deployment pipelines and host applications securely using platforms like Vercel, Netlify, or VPS with Docker.',
  keyBenefits: [
    'CI/CD workflows with GitHub Actions',
    'Custom domain and SSL integration',
    'Zero-downtime deployment strategies',
    'Logging, monitoring, and alerts'
  ],
  useCases: [
    'SaaS app production pipelines',
    'Developer portfolio and showcase sites',
    'Headless CMS and Jamstack deployments',
    'Agencies delivering client apps'
  ]
},
'custom-crm': {
  title: 'Custom CRM Development',
  icon: BarChart2,
  description: 'Tailor your business operations with a CRM designed to match workflows, integrate data sources, and improve customer engagement.',
  keyBenefits: [
    'Custom pipelines and client tracking',
    'Sales automation and reminders',
    'Staff and permission modules',
    'Integrated reporting dashboards'
  ],
  useCases: [
    'B2B lead tracking systems',
    'Customer service ticketing tools',
    'Startup client management platforms',
    'Sales rep and KPI monitoring dashboards'
  ]
}


  };

  const service = serviceData[serviceType];

  if (!service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 px-4">
        <div className="text-center p-8">
          <h1 className="text-xl md:text-2xl font-bold text-gray-900 mb-4">Service Not Found</h1>
          <Link 
            to="/web-services" 
            className="inline-flex items-center text-green-600 hover:text-green-800 font-medium text-sm md:text-base"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to Web Services
          </Link>
        </div>
      </div>
    );
  }

  const Icon = service.icon;

  const styles = {
    container: {
      minHeight: '100vh',
      background: 'linear-gradient(to bottom, #f0fdf4, #dcfce7)',
      fontFamily: 'system-ui, -apple-system, sans-serif'
    },
    heroSection: {
      background: 'linear-gradient(135deg, #16a34a, #15803d)',
      color: '#ffffff',
      padding: '0',
      position: 'relative',
      overflow: 'hidden'
    },
    heroPattern: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      bottom: '0',
      backgroundImage: 'radial-gradient(circle at 1px 1px, rgba(255,255,255,0.15) 1px, transparent 0)',
      backgroundSize: '40px 40px',
      opacity: '0.3'
    },
    heroContent: {
      position: 'relative',
      zIndex: '10',
      padding: '60px 16px 40px',
      textAlign: 'center',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    backButton: {
      position: 'absolute',
      top: '12px',
      left: '12px',
      display: 'inline-flex',
      alignItems: 'center',
      color: '#ffffff',
      textDecoration: 'none',
      fontSize: '12px',
      fontWeight: '600',
      padding: '8px 12px',
      borderRadius: '12px',
      backgroundColor: 'rgba(255, 255, 255, 0.15)',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      zIndex: '20',
      transform: 'translateY(0)',
      boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
    },
    iconContainer: {
      display: 'inline-flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '60px',
      height: '60px',
      background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.2), rgba(255, 255, 255, 0.1))',
      borderRadius: '16px',
      marginBottom: '20px',
      backdropFilter: 'blur(10px)',
      border: '1px solid rgba(255, 255, 255, 0.2)',
      boxShadow: '0 8px 32px rgba(0, 0, 0, 0.1)',
      transition: 'all 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      animation: 'float 6s ease-in-out infinite'
    },
    title: {
      fontSize: '1.75rem',
      fontWeight: '900',
      marginBottom: '16px',
      lineHeight: '1.1',
      background: 'linear-gradient(135deg, #ffffff, #e2e8f0)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    description: {
      fontSize: '0.95rem',
      maxWidth: '600px',
      margin: '0 auto',
      lineHeight: '1.6',
      opacity: '0.95',
      fontWeight: '400',
      padding: '0 16px'
    },
    contentSection: {
      padding: '40px 16px',
      maxWidth: '1200px',
      margin: '0 auto'
    },
    statsSection: {
      display: 'grid',
      gridTemplateColumns: 'repeat(2, 1fr)',
      gap: '12px',
      marginBottom: '40px'
    },
    statCard: {
      background: 'linear-gradient(135deg, #ffffff, #f0fdf4)',
      padding: '20px 16px',
      borderRadius: '16px',
      textAlign: 'center',
      border: '1px solid #bbf7d0',
      boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      cursor: 'pointer',
      position: 'relative',
      overflow: 'hidden'
    },
    statIcon: {
      width: '32px',
      height: '32px',
      margin: '0 auto 12px',
      color: '#16a34a'
    },
    statTitle: {
      fontSize: '0.85rem',
      fontWeight: '600',
      color: '#374151',
      marginBottom: '6px'
    },
    statValue: {
      fontSize: '1.25rem',
      fontWeight: '700',
      background: 'linear-gradient(135deg, #16a34a, #15803d)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent'
    },
    mainGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: '24px',
      marginBottom: '60px'
    },
    card: {
      background: 'linear-gradient(135deg, #ffffff, #f0fdf4)',
      borderRadius: '20px',
      padding: '24px',
      border: '1px solid #bbf7d0',
      boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
      transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      cursor: 'pointer',
      transform: 'translateY(0)'
    },
    cardGradient: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      height: '4px',
      background: 'linear-gradient(135deg, #16a34a, #15803d)'
    },
    cardTitle: {
      fontSize: '1.25rem',
      fontWeight: '700',
      color: '#1f2937',
      marginBottom: '20px',
      display: 'flex',
      alignItems: 'center'
    },
    cardTitleIcon: {
      width: '20px',
      height: '20px',
      marginRight: '8px',
      padding: '4px',
      borderRadius: '6px',
      background: 'linear-gradient(135deg, #16a34a, #15803d)',
      color: '#ffffff'
    },
    listItem: {
      display: 'flex',
      alignItems: 'flex-start',
      marginBottom: '12px',
      fontSize: '0.95rem',
      color: '#4b5563',
      lineHeight: '1.5'
    },
    listIcon: {
      width: '16px',
      height: '16px',
      marginRight: '10px',
      marginTop: '2px',
      flexShrink: '0'
    },
    ctaSection: {
      background: 'linear-gradient(135deg, #f0fdf4, #dcfce7)',
      padding: '50px 16px',
      textAlign: 'center',
      position: 'relative'
    },
    ctaCard: {
      background: 'linear-gradient(135deg, #ffffff, #f0fdf4)',
      borderRadius: '24px',
      padding: '32px 24px',
      maxWidth: '600px',
      margin: '0 auto',
      border: '1px solid #bbf7d0',
      boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
      position: 'relative',
      overflow: 'hidden'
    },
    ctaGradient: {
      position: 'absolute',
      top: '0',
      left: '0',
      right: '0',
      height: '4px',
      background: 'linear-gradient(135deg, #16a34a, #15803d)'
    },
    ctaTitle: {
      fontSize: '1.75rem',
      fontWeight: '800',
      background: 'linear-gradient(135deg, #1f2937, #374151)',
      backgroundClip: 'text',
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      marginBottom: '16px'
    },
    ctaText: {
      fontSize: '1rem',
      color: '#6b7280',
      marginBottom: '28px',
      lineHeight: '1.6'
    },
    button: {
      background: 'linear-gradient(135deg, #16a34a, #15803d)',
      color: '#ffffff',
      fontWeight: '700',
      padding: '14px 28px',
      borderRadius: '12px',
      border: 'none',
      cursor: 'pointer',
      fontSize: '0.95rem',
      textDecoration: 'none',
      display: 'inline-flex',
      alignItems: 'center',
      boxShadow: '0 10px 15px -3px rgba(22, 163, 74, 0.4), 0 4px 6px -2px rgba(22, 163, 74, 0.2)',
      transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
      position: 'relative',
      overflow: 'hidden',
      transform: 'translateY(0)'
    }
  };

  // Responsive media queries in CSS
  const responsiveCSS = `
    /* Tablet styles */
    @media (min-width: 640px) {
      .hero-content {
        padding: 80px 20px 60px !important;
      }
      
      .icon-container {
        width: 80px !important;
        height: 80px !important;
        border-radius: 20px !important;
        margin-bottom: 24px !important;
      }
      
      .icon-container svg {
        width: 40px !important;
        height: 40px !important;
      }
      
      .hero-title {
        font-size: 2.25rem !important;
        margin-bottom: 20px !important;
      }
      
      .hero-description {
        font-size: 1.1rem !important;
        padding: 0 !important;
      }
      
      .back-button {
        top: 16px !important;
        left: 16px !important;
        font-size: 13px !important;
        padding: 10px 16px !important;
      }
      
      .stats-section {
        grid-template-columns: repeat(4, 1fr) !important;
        gap: 16px !important;
        margin-bottom: 60px !important;
      }
      
      .stat-card {
        padding: 24px 16px !important;
      }
      
      .stat-icon {
        width: 40px !important;
        height: 40px !important;
        margin-bottom: 16px !important;
      }
      
      .stat-title {
        font-size: 0.9rem !important;
        margin-bottom: 8px !important;
      }
      
      .stat-value {
        font-size: 1.4rem !important;
      }
      
      .content-section {
        padding: 60px 20px !important;
      }
      
      .main-grid {
        grid-template-columns: repeat(2, 1fr) !important;
        gap: 32px !important;
        margin-bottom: 80px !important;
      }
      
      .main-card {
        padding: 32px !important;
      }
      
      .card-title {
        font-size: 1.5rem !important;
        margin-bottom: 24px !important;
      }
      
      .card-title-icon {
        width: 24px !important;
        height: 24px !important;
        margin-right: 12px !important;
        padding: 6px !important;
        border-radius: 8px !important;
      }
      
      .list-item {
        font-size: 1rem !important;
        margin-bottom: 14px !important;
      }
      
      .list-icon {
        width: 18px !important;
        height: 18px !important;
        margin-right: 12px !important;
      }
      
      .cta-section {
        padding: 70px 20px !important;
      }
      
      .cta-card {
        padding: 48px 32px !important;
        max-width: 700px !important;
      }
      
      .cta-title {
        font-size: 2.25rem !important;
        margin-bottom: 20px !important;
      }
      
      .cta-text {
        font-size: 1.15rem !important;
        margin-bottom: 36px !important;
      }
      
      .cta-button {
        font-size: 1rem !important;
        padding: 16px 32px !important;
      }
    }
    
    /* Desktop styles */
    @media (min-width: 1024px) {
      .hero-content {
        padding: 120px 20px 80px !important;
      }
      
      .icon-container {
        width: 100px !important;
        height: 100px !important;
        border-radius: 24px !important;
        margin-bottom: 32px !important;
      }
      
      .icon-container svg {
        width: 50px !important;
        height: 50px !important;
      }
      
      .hero-title {
        font-size: clamp(2.5rem, 5vw, 4rem) !important;
        margin-bottom: 24px !important;
      }
      
      .hero-description {
        font-size: 1.3rem !important;
        max-width: 800px !important;
      }
      
      .back-button {
        top: 20px !important;
        left: 20px !important;
        font-size: 14px !important;
        padding: 12px 20px !important;
      }
      
      .stats-section {
        gap: 24px !important;
      }
      
      .stat-card {
        padding: 32px 24px !important;
      }
      
      .stat-icon {
        width: 48px !important;
        height: 48px !important;
      }
      
      .stat-title {
        font-size: 1rem !important;
      }
      
      .stat-value {
        font-size: 1.5rem !important;
      }
      
      .content-section {
        padding: 100px 20px !important;
      }
      
      .main-grid {
        grid-template-columns: repeat(auto-fit, minmax(500px, 1fr)) !important;
        gap: 40px !important;
      }
      
      .main-card {
        padding: 40px !important;
      }
      
      .card-title {
        font-size: 1.75rem !important;
      }
      
      .card-title-icon {
        width: 28px !important;
        height: 28px !important;
      }
      
      .list-item {
        font-size: 1.1rem !important;
        margin-bottom: 16px !important;
      }
      
      .list-icon {
        width: 20px !important;
        height: 20px !important;
      }
      
      .cta-section {
        padding: 80px 20px !important;
      }
      
      .cta-card {
        padding: 60px 40px !important;
      }
      
      .cta-title {
        font-size: 2.5rem !important;
      }
      
      .cta-text {
        font-size: 1.2rem !important;
        margin-bottom: 40px !important;
      }
      
      .cta-button {
        font-size: 1.1rem !important;
        padding: 18px 40px !important;
      }
    }
  `;

  return (
    <div style={styles.container}>
      <style>
        {`
          @keyframes float {
            0%, 100% { transform: translateY(0px) rotate(0deg); }
            50% { transform: translateY(-10px) rotate(2deg); }
          }
          
          @keyframes pulse {
            0%, 100% { opacity: 1; }
            50% { opacity: 0.8; }
          }
          
          @keyframes shimmer {
            0% { background-position: -200px 0; }
            100% { background-position: calc(200px + 100%) 0; }
          }
          
          .back-button:hover {
            background-color: rgba(255, 255, 255, 0.25) !important;
            transform: translateY(-2px) !important;
            box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15) !important;
          }
          
          .icon-container:hover {
            transform: translateY(-8px) scale(1.05) !important;
            box-shadow: 0 20px 40px rgba(0, 0, 0, 0.15) !important;
            background: linear-gradient(135deg, rgba(255, 255, 255, 0.3), rgba(255, 255, 255, 0.2)) !important;
          }
          
          .stat-card:hover {
            transform: translateY(-8px) scale(1.02) !important;
            box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
            border-color: #16a34a !important;
          }
          
          .stat-card:hover .stat-icon {
            animation: pulse 2s infinite;
            transform: scale(1.1);
          }
          
          .main-card:hover {
            transform: translateY(-12px) !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25) !important;
            border-color: #16a34a !important;
          }
          
          .main-card:hover .card-gradient {
            height: 6px !important;
            background: linear-gradient(135deg, #059669, #047857) !important;
          }
          
          .main-card:hover .card-title-icon {
            transform: scale(1.1) rotate(5deg);
          }
          
          .cta-button:hover {
            transform: translateY(-4px) scale(1.05) !important;
            box-shadow: 0 20px 25px -5px rgba(22, 163, 74, 0.6), 0 10px 10px -5px rgba(22, 163, 74, 0.4) !important;
            background: linear-gradient(135deg, #059669, #047857) !important;
          }
          
          .cta-button:hover .button-arrow {
            transform: translateX(5px) rotate(180deg);
          }
          
          .list-item:hover .list-icon {
            transform: scale(1.2);
            transition: transform 0.3s ease;
          }
          
          .cta-card:hover {
            transform: translateY(-5px) !important;
            box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.15) !important;
          }
          
          ${responsiveCSS}
        `}
      </style>
      
      <div style={styles.heroSection}>
        <div style={styles.heroPattern}></div>
        
        <Link to="/web-services" style={styles.backButton} className="back-button">
          <ArrowLeft style={{ width: '14px', height: '14px', marginRight: '6px' }} />
          Back to Web Services
        </Link>
        
        <div style={styles.heroContent} className="hero-content">
          <div style={styles.iconContainer} className="icon-container">
            <Icon style={{ width: '30px', height: '30px', color: '#ffffff' }} />
          </div>
          
          <h1 style={styles.title} className="hero-title">{service.title}</h1>
          <p style={styles.description} className="hero-description">{service.description}</p>
        </div>
      </div>

      <div style={styles.contentSection} className="content-section">
              {/* Stats Section */}
              <div style={styles.statsSection} className="stats-section">
                <div style={styles.statCard} className="stat-card">
                  <Zap style={{...styles.statIcon}} className="stat-icon" />
                  <div style={styles.statTitle} className="stat-title">Performance</div>
                  <div style={styles.statValue} className="stat-value">99.9%</div>
                </div>
                <div style={styles.statCard} className="stat-card">
                  <Star style={{...styles.statIcon}} className="stat-icon" />
                  <div style={styles.statTitle} className="stat-title">Success Rate</div>
                  <div style={styles.statValue} className="stat-value">95%+</div>
                </div>
                <div style={styles.statCard} className="stat-card">
                  <TrendingUp style={{...styles.statIcon}} className="stat-icon" />
                  <div style={styles.statTitle} className="stat-title">ROI Increase</div>
                  <div style={styles.statValue} className="stat-value">250%</div>
                </div>
                <div style={styles.statCard} className="stat-card">
                  <Users style={{...styles.statIcon}} className="stat-icon" />
                  <div style={styles.statTitle} className="stat-title">Clients Served</div>
                  <div style={styles.statValue} className="stat-value">500+</div>
                </div>
              </div>
      
              {/* Main Content Grid */}
              <div style={styles.mainGrid} className="main-grid">
                <div style={styles.card} className="main-card">
                  <div style={styles.cardGradient} className="card-gradient"></div>
                  <h2 style={styles.cardTitle} className="card-title">
                    <CheckCircle style={styles.cardTitleIcon} className="card-title-icon" />
                    Key Benefits
                  </h2>
                  <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
                    {service.keyBenefits.map((benefit, index) => (
                      <li key={index} style={styles.listItem} className="list-item">
                        <CheckCircle style={{ ...styles.listIcon, color: '#10b981' }} className="list-icon" />
                        {benefit}
                      </li>
                    ))}
                  </ul>
                </div>
      
                <div style={styles.card} className="main-card">
                  <div style={styles.cardGradient} className="card-gradient"></div>
                  <h2 style={styles.cardTitle} className="card-title">
                    <Lightbulb style={styles.cardTitleIcon} className="card-title-icon" />
                    Real-World Applications
                  </h2>
                  <ul style={{ listStyle: 'none', padding: '0', margin: '0' }}>
                    {service.useCases.map((useCase, index) => (
                      <li key={index} style={styles.listItem} className="list-item">
                        <Lightbulb style={{ ...styles.listIcon, color: '#f59e0b' }} className="list-icon" />
                        {useCase}
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
      
            <div style={styles.ctaSection} className="cta-section">
              <div style={{...styles.ctaCard, transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)'}} className="cta-card">
                <div style={styles.ctaGradient}></div>
                <h2 style={styles.ctaTitle} className="cta-title">Ready to Transform Your Business?</h2>
                <p style={styles.ctaText} className="cta-text">
                  Let's discuss how {service.title.toLowerCase()} can revolutionize your operations, 
                  boost efficiency, and drive unprecedented growth for your organization.
                </p>
                <Link to="/contact" style={styles.button} className="cta-button">
                  Get Started Today
                  <ArrowLeft style={{ width: '18px', height: '18px', marginLeft: '8px', transform: 'rotate(180deg)', transition: 'transform 0.3s ease' }} className="button-arrow" />
                </Link>
              </div>
            </div>
          </div>
        );
      }